import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxCurrencyConfig } from 'ngx-currency';
import { catchError, finalize, forkJoin, merge, of } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TabsScrollDirective } from '@shared/directives/tabs-scroll.directive';
import { RegimeTributarioEnum } from '@shared/enums/motor-fiscal/regime-tributario.enum';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { LocalizacaoEstoque } from '@shared/services/models/produto/localizacao-estoque.model';
import { LOOKUPS_FISCAIS_VAZIOS, LookupsFiscais } from '@shared/services/structs/motor-fiscal/lookups-fiscais.struct';
import { VendaItemRastreabilidade } from '@shared/services/models/vendas/venda-item-rastreabilidade.model';
import { VendaItemSerie } from '@shared/services/models/vendas/venda-item-serie.model';
import { montarFormItemVenda } from '../venda-item.form';
import { motivoDaSelecaoDeLoteSerie, motivoDeExcessoDeLoteSerie } from '../venda-item.lote-serie';
import { recalcularValoresDoItem } from '../venda-item.calculo';
import { VdItemAbaCofinsComponent } from './abas/vd-item-aba-cofins/vd-item-aba-cofins.component';
import { VdItemAbaComissaoComponent } from './abas/vd-item-aba-comissao/vd-item-aba-comissao.component';
import { VdItemAbaDadosComponent } from './abas/vd-item-aba-dados/vd-item-aba-dados.component';
import { VdItemAbaDifalComponent } from './abas/vd-item-aba-difal/vd-item-aba-difal.component';
import { VdItemAbaIbsCbsComponent } from './abas/vd-item-aba-ibs-cbs/vd-item-aba-ibs-cbs.component';
import { VdItemAbaIcmsComponent } from './abas/vd-item-aba-icms/vd-item-aba-icms.component';
import { VdItemAbaIpiComponent } from './abas/vd-item-aba-ipi/vd-item-aba-ipi.component';
import { VdItemAbaLoteSerieComponent } from './abas/vd-item-aba-lote-serie/vd-item-aba-lote-serie.component';
import { VdItemAbaOutrosComponent } from './abas/vd-item-aba-outros/vd-item-aba-outros.component';
import { VdItemAbaPisComponent } from './abas/vd-item-aba-pis/vd-item-aba-pis.component';
import { VdItemAbaSimplesComponent } from './abas/vd-item-aba-simples/vd-item-aba-simples.component';

/** O que a seção de itens entrega ao modal. */
export interface VdItemDialogData {
    /** O FormGroup da linha. O modal NÃO escreve nele até o "Confirmar". */
    linha: FormGroup;
    ehOrcamento: boolean;
    calcularImpostos: boolean;
    isSomenteLeitura: boolean;
    exibeDescontoPorItem: boolean;
    tetoDesconto: number | null;

    /** A natureza do documento: o regime tributário dela decide entre as abas Simples e ICMS. */
    idNatOpDocumento: number | null;

    /** Depósitos ativos da empresa, para o select "Localização" da aba Dados do item. */
    localizacoesEstoque: LocalizacaoEstoque[];

    /** Documento já gravado: a reserva dele volta ao saldo dos lotes e séries da aba Lote/série (T21). */
    idVenda: number | null;

    mascaraPreco: Partial<NgxCurrencyConfig>;
    mascaraQuantidade: Partial<NgxCurrencyConfig>;
    mascaraInteira: Partial<NgxCurrencyConfig>;
    mascaraPercentual: Partial<NgxCurrencyConfig>;

}

/**
 * Grupo de tributo do form e a chave gravada em `tributos_informados_manualmente` (comentário da
 * coluna em `venda_item`). A ordem é a das abas de tributo.
 */
const TRIBUTO_POR_GRUPO: ReadonlyArray<[grupo: string, chave: string]> = [
    ['icms', 'icms'],
    ['ipi', 'ipi'],
    ['pis', 'pis'],
    ['cofins', 'cofins'],
    ['ibsCbs', 'ibs_cbs'],
    ['difal', 'difal'],
];

/**
 * Modal do item da venda (orçamento e pedido), na organização do modal do item da NF-e de saída:
 * cabeçalho com a descrição do item e a identificação (SKU e EAN) da lista, avisos, abas (Dados do
 * item, Simples ou ICMS pelo regime, IPI, PIS, COFINS, IBS/CBS, ICMS DIFAL, Lote/série, Outros e
 * Comissão) e rodapé com a origem dos valores. Lote/série só existe para produto que controla lote ou
 * série, e o "Confirmar" exige que a seleção feche a quantidade do item.
 *
 * As abas de tributo só existem com o cálculo de impostos ligado no processo; Outros e Comissão só
 * no pedido. Edita uma CÓPIA: "Cancelar" descarta, e só "Confirmar" aplica na linha (dados e grupos
 * de tributo), depois de validar descrição, quantidade, preço e os campos que o CST exige. Sem
 * alteração, só fecha. `recalcularValoresDoItem` acompanha a edição (total e comissão); os tributos
 * não são calculados na tela, ficam com o que foi informado até o motor fiscal.
 */
@Component({
    selector: 'app-vd-item-dialog',
    standalone: true,
    imports: [
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTabsModule,
        TabsScrollDirective,
        AlertCardComponent,
        LoadingBoxComponent,
        VdItemAbaCofinsComponent,
        VdItemAbaComissaoComponent,
        VdItemAbaDadosComponent,
        VdItemAbaDifalComponent,
        VdItemAbaIbsCbsComponent,
        VdItemAbaIcmsComponent,
        VdItemAbaIpiComponent,
        VdItemAbaLoteSerieComponent,
        VdItemAbaOutrosComponent,
        VdItemAbaPisComponent,
        VdItemAbaSimplesComponent,
    ],
    templateUrl: './vd-item-dialog.component.html',
    styleUrl: './vd-item-dialog.component.css',
})
export class VdItemDialogComponent implements OnInit {
    /** A cópia em edição. */
    public item!: FormGroup;

    /** Catálogos de CST, CSOSN, IBS/CBS, naturezas, origens e tipos de item SPED do Motor Fiscal. */
    public lookups: LookupsFiscais = LOOKUPS_FISCAIS_VAZIOS;
    public isCarregandoListas: boolean = false;
    public isListasIndisponiveis: boolean = false;

    /** Só na primeira carga das listas: o "Tentar novamente" mantém as abas à mostra. */
    public isLoading: boolean = true;
    public isCarregandoRegime: boolean = true;

    /** Código do regime tributário da natureza do documento: decide entre as abas Simples e ICMS. */
    public codigoRegimeTributario: string | null = null;

    /** O item mudou depois de aberto: os valores apurados ficam desatualizados até o recálculo. */
    public isRecalculoPendente: boolean = false;

    public abaSelecionada: number = 0;

    /** O estado da cópia ao abrir, para "sem alteração, só fecha". */
    private estadoInicial: string = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: VdItemDialogData,
        private dialogRef: MatDialogRef<VdItemDialogComponent, boolean>,
        private fb: FormBuilder,
        private alertService: AlertService,
        private motorFiscalLookupService: MotorFiscalLookupService,
    ) {}

    public ngOnInit(): void {
        this.item = montarFormItemVenda(this.fb);
        this.item.patchValue(this.data.linha.getRawValue(), { emitEvent: false });

        this.aplicarBloqueios();

        // Documento em leitura: desabilita a cópia inteira, em vez de `[disabled]` campo a campo.
        if (this.data.isSomenteLeitura) this.item.disable({ emitEvent: false });

        this.estadoInicial = JSON.stringify(this.item.getRawValue());

        // Quantidade e preço mexem no total e na comissão do item.
        this.item.valueChanges.subscribe(() => {
            this.isRecalculoPendente = true;
            recalcularValoresDoItem(this.item);
        });
        this.ligarTributosInformadosManualmente();

        // O saldo do lote e da série é por localização: trocá-la descarta a seleção feita na anterior. Só
        // a troca de fato: o select pode regravar o mesmo valor ao montar.
        let localizacaoAnterior = this.item.get('dados.idLocalizacaoEstoque')?.value ?? null;

        this.item.get('dados.idLocalizacaoEstoque')?.valueChanges.subscribe((idLocalizacaoEstoque) => {
            if ((idLocalizacaoEstoque ?? null) === localizacaoAnterior) return;

            localizacaoAnterior = idLocalizacaoEstoque ?? null;
            this.item.patchValue({ lotes: [], series: [] });
        });

        this.carregarRegime();
        this.carregarLookups();
    }

    /** Sem id positivo o item não foi gravado: inclui os itens de um documento copiado, que chegam com id 0. */
    public get isItemNovo(): boolean {
        return !(Number(this.item?.get('dados.idVendaItem')?.value) > 0);
    }

    /** CRT 1 (Simples Nacional) e 4 (MEI) abrem a aba Simples; os demais abrem a ICMS. */
    public get isSimplesNacional(): boolean {
        return this.codigoRegimeTributario === String(RegimeTributarioEnum.SimplesNacional)
            || this.codigoRegimeTributario === String(RegimeTributarioEnum.MicroempreendedorIndividual);
    }

    /** Tributos só com o cálculo de impostos ligado no processo (§6.1). */
    public get mostraAbasFiscais(): boolean {
        return this.data.calcularImpostos;
    }

    /** "Outros" e comissão são do PEDIDO: o orçamento não gera nota nem paga comissão. */
    public get mostraAbasDePedido(): boolean {
        return !this.data.ehOrcamento;
    }

    public get isControlaLotes(): boolean {
        return this.item?.get('dados.isControlaLotes')?.value === true;
    }

    public get isControlaSeries(): boolean {
        return this.item?.get('dados.isControlaSeries')?.value === true;
    }

    /** Só para produto que controla lote ou série (cadastro do produto). */
    public get mostraAbaLoteSerie(): boolean {
        return this.isControlaLotes || this.isControlaSeries;
    }

    public get nomeLocalizacaoEstoque(): string {
        const id = this.item?.get('dados.idLocalizacaoEstoque')?.value;

        return this.data.localizacoesEstoque.find((l) => l.idLocalizacaoEstoque === id)?.nome ?? '';
    }

    /**
     * Só o ícone da aba: o pedido salva sem lote e série (D5), e é o lançar estoque que cobra a seleção, com o lote da
     * reserva ou a sugestão já preenchidos. A conferência aqui avisa o que ainda falta, sem travar o Confirmar.
     */
    public get motivoLoteSerie(): string | null {
        return motivoDaSelecaoDeLoteSerie(this.item, this.data.mascaraQuantidade?.precision ?? 2);
    }

    public aoAlterarLotes(lotes: VendaItemRastreabilidade[]): void {
        this.item.get('lotes')?.setValue(lotes);
    }

    public aoAlterarSeries(series: VendaItemSerie[]): void {
        this.item.get('series')?.setValue(series);
    }

    public grupo(nome: string): FormGroup {
        return this.item.get(nome) as FormGroup;
    }

    public get tituloModal(): string {
        return this.item?.get('dados.descricao')?.value || (this.isItemNovo ? 'Novo item' : 'Item');
    }

    /** "SKU 123 · EAN 789": a mesma identificação que a lista mostra abaixo da descrição. */
    public get identificacao(): string {
        const sku = this.item?.get('dados.codigoSku')?.value;
        const ean = this.item?.get('dados.gtinEan')?.value;

        return [sku ? `SKU ${sku}` : null, ean ? `EAN ${ean}` : null].filter(Boolean).join(' · ');
    }

    /** O ícone de erro aparece na aba cujo grupo do form está inválido. */
    public isAbaInvalida(nomeGrupo: string): boolean {
        return this.item.get(nomeGrupo)?.invalid ?? false;
    }

    /** Antes do motor fiscal da venda não há cálculo salvo para mostrar. */
    public get origemValoresApurados(): string {
        if (this.isRecalculoPendente) return 'Recálculo pendente';

        return 'Sem cálculo salvo';
    }

    public get textoRodape(): string {
        const idNatOp = this.item.get('dados.idNatOp')?.value ?? this.data.idNatOpDocumento;
        const natureza = this.lookups.naturezas.find((n) => n.id === idNatOp)?.descricao;

        const base = natureza
            ? `Os tributos deste item são calculados pela natureza ${natureza}.`
            : 'Os tributos deste item são calculados pela natureza do documento.';

        // §7.2 / CA 13c: no orçamento a apuração é estimativa; a definitiva é a da nota.
        return this.data.ehOrcamento
            ? `${base} Valores estimados pela natureza da operação. A apuração definitiva ocorre na emissão da nota fiscal.`
            : base;
    }

    /**
     * As listas só alimentam os selects: o catálogo fora do ar não impede editar o item. O serviço não
     * guarda em cache a resposta incompleta, então "Tentar novamente" busca de novo.
     */
    public carregarLookups(): void {
        this.isCarregandoListas = true;

        this.motorFiscalLookupService.getVendaItemLookup()
            .pipe(finalize(() => {
                this.isCarregandoListas = false;
                this.isLoading = false;
            }))
            .subscribe((lookups) => {
                this.lookups = lookups;
                this.isListasIndisponiveis = this.mostraAbasFiscais
                    && [lookups.cstsIcms, lookups.csosns, lookups.cstsIpi, lookups.cstsPis, lookups.cstsCofins, lookups.cstsIbsCbs]
                        .some((lista) => lista.length === 0);

                if (this.isListasIndisponiveis) {
                    this.alertService.show('Erro', 'Não foi possível carregar as listas de tributos do Motor Fiscal.', AlertType.error);
                }
            });
    }

    /**
     * Regime tributário da natureza do documento, como na NF-e de saída (`nfe-dados`): o
     * `idRegimeTributario` da natureza e o código do domínio de regimes. Sem natureza ou sem
     * resposta, o modal abre a aba ICMS.
     */
    private carregarRegime(): void {
        if (!this.data.idNatOpDocumento) {
            this.isCarregandoRegime = false;
            return;
        }

        forkJoin({
            naturezas: this.motorFiscalLookupService.getNaturezaOperacaoDocumentoLookup(TipoOperacaoEnum.Saida).pipe(catchError(() => of(null))),
            dominios: this.motorFiscalLookupService.getDominios().pipe(catchError(() => of(null))),
        })
            .pipe(finalize(() => (this.isCarregandoRegime = false)))
            .subscribe(({ naturezas, dominios }) => {
                const natureza = (naturezas?.lista ?? []).find((n) => n.idNaturezaOperacao === this.data.idNatOpDocumento);
                const regime = natureza ? (dominios?.regimes ?? []).find((r) => r.id === natureza.idRegimeTributario) : undefined;

                if (!regime) return;

                this.codigoRegimeTributario = regime.codigo;
            });
    }

    /** O motor fiscal da venda ainda não existe: o botão só avisa. */
    public recalcularTributos(): void {
        this.alertService.show('Recalcular tributos', 'O recálculo dos tributos ainda não está disponível.', AlertType.info);
    }

    /**
     * O que não se edita no modal fica `disabled` na cópia (Pedro, 24/09: "para ficar visível que
     * não é interagível"): campos do cadastro do produto nunca; descrição e quantidade no componente
     * de kit; quantidade no múltiplo (§6.2.3); preço e desconto no componente de kit de preço fixo
     * (§6.9). `getRawValue()` na ida e na volta mantém os valores dos desabilitados.
     */
    private aplicarBloqueios(): void {
        const dados = this.item.get('dados') as FormGroup;
        const desabilitar = (nomes: string[]) => nomes.forEach((n) => dados.get(n)?.disable({ emitEvent: false }));

        desabilitar(['codigoSku', 'codigoNcm', 'siglaUnidade', 'precoBrutoUnitario']);

        const ehComponenteDeKit = this.item.get('kit.numeroKit')?.value != null;

        if (ehComponenteDeKit) desabilitar(['descricao', 'quantidade']);

        if (ehComponenteDeKit && this.item.get('kit.isPrecoFixoKit')?.value === true) {
            desabilitar(['precoLiquidoUnitario', 'descontoPercentual']);
        }

        if (this.item.get('multiplo.isControlaMultiplo')?.value === true) desabilitar(['quantidade']);
    }

    /**
     * Editar um grupo de tributo marca o grupo como informado à
     * mão (sem repetir), para o cálculo preservar o que o usuário digitou. A origem da mercadoria
     * mora no grupo `icms`, mas é classificação do item (aba Dados do item) e não marca o ICMS.
     */
    private ligarTributosInformadosManualmente(): void {
        const marcados = this.item.get('dados.tributosInformadosManualmente');

        for (const [nomeGrupo, chave] of TRIBUTO_POR_GRUPO) {
            const alteracoes = Object.entries(this.grupo(nomeGrupo).controls)
                .filter(([nome]) => nome !== 'codigoOrigemMercadoria')
                .map(([, controle]) => controle.valueChanges);

            merge(...alteracoes).subscribe(() => {
                const atuais = (marcados?.value as string[] | null) ?? [];

                if (!atuais.includes(chave)) marcados?.setValue([...atuais, chave]);
            });
        }
    }

    public salvar(): void {
        if (this.data.isSomenteLeitura) return;

        const motivo = this.motivoParaNaoSalvar();

        if (motivo) {
            this.abaSelecionada = 0;
            this.alertService.show('Não foi possível salvar o item', motivo, AlertType.warning);
            return;
        }

        // A obrigatoriedade vem dos indicadores do CST de cada tributo, como na natureza de operação.
        this.item.markAllAsTouched();

        if (this.item.invalid) {
            this.navegarParaPrimeiraAbaInvalida();
            this.alertService.show(
                'Não foi possível salvar o item',
                'Preencha os campos obrigatórios dos tributos, conforme o CST informado, e corrija os valores negativos.',
                AlertType.warning,
            );
            return;
        }

        if (JSON.stringify(this.item.getRawValue()) === this.estadoInicial) {
            this.dialogRef.close(false);
            return;
        }

        // A linha recebe a cópia inteira: dados do item e grupos de tributo editados nas abas.
        this.data.linha.patchValue(this.item.getRawValue());
        this.data.linha.markAsDirty();
        this.dialogRef.close(true);
    }

    /**
     * A ordem espelha a dos `mat-tab` do template: Dados do item e, com o cálculo de impostos ligado,
     * ICMS/Simples, IPI, PIS, COFINS, IBS/CBS e ICMS DIFAL.
     */
    private navegarParaPrimeiraAbaInvalida(): void {
        const posicao = this.mostraAbasFiscais ? TRIBUTO_POR_GRUPO.findIndex(([nomeGrupo]) => this.isAbaInvalida(nomeGrupo)) : -1;

        this.abaSelecionada = posicao >= 0 ? posicao + 1 : 0;
    }

    private motivoParaNaoSalvar(): string | null {
        const dados = this.item.get('dados') as FormGroup;

        if (!String(dados.get('descricao')?.value ?? '').trim()) return 'Informe a descrição do item.';

        if (!(Number(dados.get('quantidade')?.value ?? 0) > 0)) return 'A quantidade deve ser maior que zero.';

        // No componente de kit de preço fixo o preço vem do rateio e não se edita aqui.
        const preco = dados.get('precoLiquidoUnitario');

        if (preco?.enabled && !(Number(preco.value ?? 0) > 0)) return 'O preço unitário deve ser maior que zero.';

        // Lote e série não são obrigatórios (D5), mas a seleção não pode passar da quantidade: o servidor recusa.
        return motivoDeExcessoDeLoteSerie(this.item, this.data.mascaraQuantidade?.precision ?? 2);
    }

    public fechar(): void {
        this.dialogRef.close(false);
    }
}
